export const MAX_PIN_FAILURES = 5
export const LOCK_DURATION_MS = 15 * 60 * 1_000

export type MemberRole = 'participant' | 'admin'
export type MemberStatus = 'invited' | 'active' | 'locked' | 'archived'
export type SessionStatus = 'active' | 'archived_read_only'
export type AccessErrorCode =
  | 'INVALID_CREDENTIALS'
  | 'LOCKED'
  | 'REVOKED'
  | 'FORBIDDEN'
  | 'CAPACITY_REACHED'
  | 'CONFIGURATION'
  | 'UNAVAILABLE'

export type PinThrottleState = {
  failedAttempts: number
  lockedUntil: number | null
}

export type SessionView = {
  sessionId: string
  tripId: string
  memberId: string
  displayName: string
  role: MemberRole
  status: SessionStatus
}

export type MemberView = {
  id: string
  displayName: string
  role: MemberRole
  status: MemberStatus
}

export type MemberSessionView = {
  id: string
  memberId: string
  displayName: string
  deviceLabel: string | null
  createdAt: string
  lastSeenAt: string
  revokedAt: string | null
}

export type AccessRequest =
  | {
      action: 'join' | 'login'
      tripCode: string
      displayName: string
      pin: string
      deviceLabel?: string
    }
  | { action: 'refresh'; sessionId?: string }
  | {
      action: 'activate_admin'
      activationSecret: string
      displayName: string
      pin: string
      deviceLabel?: string
    }

export type AccessGateway = {
  readonly mode: 'demo' | 'supabase'
  selectMember(memberId: string, deviceLabel?: string): Promise<SessionView>
  access(request: AccessRequest): Promise<SessionView>
  refresh(sessionId: string): Promise<SessionView>
  listMembers(): Promise<MemberView[]>
  listSessions(): Promise<MemberSessionView[]>
  resetMemberPin(memberId: string, pin: string): Promise<void>
  revokeSession(sessionId: string): Promise<void>
  archiveMember(memberId: string): Promise<void>
}

export class AccessGatewayError extends Error {
  readonly code: AccessErrorCode
  readonly retryAfterMs?: number

  constructor(code: AccessErrorCode, message: string, options?: { retryAfterMs?: number }) {
    super(message)
    this.name = 'AccessGatewayError'
    this.code = code
    if (options?.retryAfterMs !== undefined) this.retryAfterMs = options.retryAfterMs
  }
}

export function isPinLocked(state: PinThrottleState, now = Date.now()): boolean {
  return state.lockedUntil !== null && state.lockedUntil > now
}

export function recordPinFailure(
  state: PinThrottleState,
  now = Date.now(),
): PinThrottleState {
  if (isPinLocked(state, now)) return state
  const failedAttempts = Math.min(MAX_PIN_FAILURES, state.failedAttempts + 1)
  return {
    failedAttempts,
    lockedUntil: failedAttempts === MAX_PIN_FAILURES ? now + LOCK_DURATION_MS : null,
  }
}

export function recordPinSuccess(state: PinThrottleState): PinThrottleState {
  void state
  return { failedAttempts: 0, lockedUntil: null }
}
